<template>
  <div>
    <OverweightFilter></OverweightFilter>

    <v-data-table class="elevation-1 mb-5"
      :headers="headers"
      :items="rebatedOverweights"
      :loading="loading"
      :no-data-text="$t('noDataAvailable')">
      <v-progress-linear v-slot:progress color="primary" indeterminate></v-progress-linear>

      <template v-slot:items="props">
        <tr>
          <td>
            <ShipmentLink :item="props.item"></ShipmentLink>
          </td>
          <td> {{ props.item.tracking_number }} </td>
          <td v-if="!$auth.isCust()"> {{ props.item.userEmail }} </td>
          <td> {{ props.item.overweight }} kg </td>
          <td> {{ props.item.amount | currency }} </td>
          <td> {{ $t(`list.statuses.${props.item.status}`) }} </td>
          <td> {{ props.item.created_at | date('short') }} </td>
          <td class="pa-0 pr-2 text-xs-right">
            <OverweightRowButtons :canRebate="canRebate" :overweight="props.item" @fetch="refresh"></OverweightRowButtons>
          </td>

          <td class="pa-0 pr-2 text-xs-right">
            <v-btn color="primary" flat icon v-if="!props.expanded" @click="props.expanded = !props.expanded">
              <font-awesome-icon icon="angle-left"></font-awesome-icon>
            </v-btn>

            <v-btn color="primary" flat icon v-if="props.expanded" @click="props.expanded = !props.expanded">
              <font-awesome-icon icon="angle-down"></font-awesome-icon>
            </v-btn>
          </td>
        </tr>
      </template>

      <template v-slot:expand="props">
        <OverweightRow :overweight="props.item" @fetch="fetch"></OverweightRow>
      </template>
    </v-data-table>

    <v-data-table class="elevation-1"
      :headers="headers"
      :items="items"
      :loading="loading"
      :total-items="totalItems"
      :pagination.sync="pagination"
      :no-data-text="$t('noDataAvailable')">
      <v-progress-linear v-slot:progress color="primary" indeterminate></v-progress-linear>

      <template v-slot:items="props">
        <tr>
          <td>
            <ShipmentLink :item="props.item"></ShipmentLink>
          </td>
          <td> {{ props.item.tracking_number }} </td>
          <td v-if="!$auth.isCust()"> {{ props.item.userEmail }} </td>
          <td> {{ props.item.overweight }} kg </td>
          <td> {{ props.item.amount | currency }} </td>
          <td> {{ $t(`list.statuses.${props.item.status}`) }} </td>
          <td> {{ props.item.shipment.generated_label.created_at | date('short') }} </td>
          <td class="pa-0 pr-2 text-xs-right">
            <OverweightRowButtons :canRebate="canRebate" :overweight="props.item" @fetch="fetch"></OverweightRowButtons>
          </td>

          <td class="pa-0 pr-2 text-xs-right">
            <v-btn color="primary" flat icon v-if="!props.expanded" @click="props.expanded = !props.expanded">
              <font-awesome-icon icon="angle-left"></font-awesome-icon>
            </v-btn>

            <v-btn color="primary" flat icon v-if="props.expanded" @click="props.expanded = !props.expanded">
              <font-awesome-icon icon="angle-down"></font-awesome-icon>
            </v-btn>
          </td>
        </tr>
      </template>

      <template v-slot:expand="props">
        <OverweightRow :overweight="props.item" @fetch="refresh"></OverweightRow>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import Overweight from '@/client/overweight'

import OverweightFilter from '@/components/overweights/OverweightFilterComp'
import OverweightRow from '@/components/overweights/OverweightRowComp'
import OverweightRowButtons from '@/components/overweights/OverweightRowButtonsComp'

import ShipmentLink from '@/components/ShipmentLinkComp'

export default {
  name: "OverweighListComp",

  data () {
    return {
      headers: [
        { text: this.$tc('models.shipment'), align: 'left', sortable: false, value: 'shipment_id' },
        { text: this.$t('attrs.tracking_number'), align: 'left', sortable: false, value: 'tracking_number' },
        { text: this.$tc('models.user'), align: 'left', sortable: false, value: 'userEmail' },
        { text: this.$tc('models.overweight'), align: 'left', sortable: false, value: 'overweight' },
        { text: this.$t('attrs.amount'), align: 'left', sortable: false, value: 'amount' },
        { text: this.$t('attrs.status'), align: 'left', sortable: false, value: 'status' },
        { text: this.$t('attrs.created_at'), align: 'left', sortable: false, value: 'created_at' },
        { text: '', align: 'left', sortable: false, value: 'actions' },
      ],
      rebatedOverweights: [],
      items: [],
      totalItems: 0,
      pagination: {
        rowsPerPage: 10,
        sortBy: 'created_at',
        descending: true
      },
      loading: false,
    }
  },

  props: {
    canRebate: {
      type: Boolean,
      default: false
    },
  },

  components: {
    OverweightFilter,
    OverweightRow,
    OverweightRowButtons,
    ShipmentLink,
  },

  computed: {
    where () {
      const obj = { status: 2 }
      if (this.$route.query.shipmentId) { obj.shipment_id = this.$route.query.shipmentId }
      if (this.$route.query.trackingNumber) { obj.tracking_number = this.$route.query.trackingNumber }

      if (this.$route.query.email || this.$route.query.carrierId || this.$route.query.serviceId) {
        obj.shipments = {}

        if (this.$route.query.carrierId) {
          obj.shipments.rates = {
            services: { carrier_id: this.$route.query.carrierId }
          }
        }

        if (this.$route.query.serviceId) {
          obj.shipments.rates = {
            service_id: this.$route.query.serviceId
          }
        }

        if (this.$route.query.email) {
          obj.shipments.users = {
            email: this.$route.query.email
          }
        }
      }

      return obj
    }
  },

  methods: {
    async fetchRebatedOverweights () {
      this.loading = true
      const obj = { where: this.where, }

      this.rebatedOverweights = await Overweight.all(obj)
      this.loading = false
    },

    async fetch () {
      this.loading = true
      const { page, rowsPerPage } = this.pagination

      const obj = {
        page,
        per_page: rowsPerPage,
        where: Object.assign({}, this.where, { status: [0, 1, 3] }),
      }

      this.items = await Overweight.all(obj)
      this.totalItems = this.items.pagination.total_objects
      this.loading = false
    },

    refresh () {
      this.fetch()
      this.fetchRebatedOverweights()
    },
  },

  watch: {
    pagination: {
      handler () {
        this.fetch()
      },
      deep: true
    },

    '$route.query': {
      handler () {
        this.refresh()
      },
      deep: true
    },
  },

  mounted () {
    this.fetch()
    this.fetchRebatedOverweights()
  }
}
</script>
